import { useCallback, useMemo } from 'react';
import { SurveyResponseStatus } from '@gokrew/models/survey';
import { DocumentRequestStatus } from '@gokrew/models/documents';
import { ReviewSubmissionStatus } from '@gokrew/models/performance';
import { pendingDocumentTasks } from '@gokrew/models/signature-requests';
import { useGetMyEmployeeTasks } from '@/hooks/UseEmployeeTasks';
import { useMySurveys } from '@/hooks/useEmployeeSurvey';
import {
  useMySignatureApprovals,
  useMySignatureRequests,
} from '@/hooks/useSignatureRequests';
import { useMyAcknowledgements } from '@/hooks/useAcknowledgements';
import { useMyDocumentRequests } from '@/hooks/useDocumentRequests';
import { usePendingTrainings } from '@/hooks/trainings/useMyTrainings';
import { useEmployeeReviewsOverview } from '@/hooks/performance/useEmployeeReviews';
import { usePermissions } from '@/context/PermissionContext';
import { permissionKeys } from '@/utility/permissions';
import { formatDate } from '@gokrew/ui/utility/utility';
import type { WorkspaceAction } from '@/types/workspace';

/** One read model over existing APIs, never a second task store. */
export const useWorkspaceActions = (includeTasks = true) => {
  const { permissions } = usePermissions();
  const canReadDocuments = permissions.has(permissionKeys.document.read);
  const tasks = useGetMyEmployeeTasks(
    {
      limit: 100,
      sortBy: 'due_date_earliest',
      status: ['not_started', 'in_progress'],
    },
    includeTasks
  );
  const pending = useMySurveys({
    status: SurveyResponseStatus.PENDING,
    limit: 100,
  });
  const started = useMySurveys({
    status: SurveyResponseStatus.IN_PROGRESS,
    limit: 100,
  });
  const signatures = useMySignatureRequests();
  const documentApprovals = useMySignatureApprovals();
  const acknowledgements = useMyAcknowledgements('pending');
  const uploads = useMyDocumentRequests(canReadDocuments);
  const trainings = usePendingTrainings();
  const reviews = useEmployeeReviewsOverview();
  const today = formatDate(new Date(), { format: 'YYYY-MM-DD' });
  const isLate = useCallback(
    (due: string | null) => !!due && due.slice(0, 10) < today,
    [today]
  );

  const rows = useMemo(() => {
    const result: WorkspaceAction[] = [];
    for (const task of tasks.data?.data.data ?? []) {
      if (task.status === 'completed' || task.status === 'submitted') continue;
      result.push({
        key: `task-${task.assignmentId}`,
        id: task.assignmentId,
        kind: 'task',
        title: task.title,
        due: task.dueDate,
        overdue: task.isOverdue,
        task,
      });
    }
    for (const recipient of [
      ...(pending.data?.data.data ?? []),
      ...(started.data?.data.data ?? []),
    ]) {
      if (
        recipient.survey.status !== 'published' ||
        recipient.survey.startsOn.slice(0, 10) > today ||
        isLate(recipient.survey.endsOn)
      )
        continue;
      result.push({
        key: `survey-${recipient.id}`,
        id: recipient.surveyId,
        kind: 'survey',
        title: recipient.survey.title,
        due: recipient.survey.endsOn,
        overdue: false,
        href: `/surveys/${recipient.surveyId}`,
      });
    }
    for (const doc of pendingDocumentTasks(
      signatures.data ?? [],
      acknowledgements.data ?? [],
      today
    )) {
      result.push({
        key: `${doc.kind}-${doc.id}`,
        id: doc.id,
        kind: doc.kind,
        title: doc.title,
        due: doc.dueDay,
        overdue: doc.overdue,
        href:
          doc.kind === 'acknowledgement'
            ? `/signatures/read/${doc.id}`
            : undefined,
      });
    }
    // Documents HR asked me to approve before they go to an employee.
    for (const approval of documentApprovals.data ?? []) {
      if (!approval.waiting) continue;
      result.push({
        key: `document-approval-${approval.id}`,
        id: approval.id,
        kind: 'document_approval',
        title: `${approval.title} · ${approval.employeeName}`,
        due: null,
        overdue: false,
        href: `/signatures/approvals/${approval.id}`,
      });
    }
    if (permissions.has(permissionKeys.document.read)) {
      for (const upload of uploads.data?.data ?? []) {
        if (upload.status !== DocumentRequestStatus.Open) continue;
        result.push({
          key: `upload-${upload.id}`,
          id: upload.id,
          kind: 'upload',
          title: upload.documentType.name,
          due: upload.dueOn,
          overdue: upload.overdue,
          href: `/my-information/documents?request=${upload.id}`,
        });
      }
    }
    for (const training of trainings.items) {
      result.push({
        key: `training-${training.id}`,
        id: training.id,
        kind: 'training',
        title: training.title,
        due: training.dueAt,
        overdue: training.overdue,
        href: `/trainings/${training.id}`,
      });
    }
    for (const review of reviews.data?.data.toWrite ?? []) {
      if (
        !review.editable ||
        review.status === ReviewSubmissionStatus.SUBMITTED
      )
        continue;
      result.push({
        key: `review-${review.submissionId}`,
        id: review.reviewId,
        kind: 'review',
        title: review.cycle.title,
        due: review.dueDate,
        overdue: isLate(review.dueDate),
        href: `/performance/reviews/submissions/${review.submissionId}`,
      });
    }
    for (const review of reviews.data?.data.sharedWithMe ?? []) {
      if (!review.requireAcknowledgement || review.acknowledgedAt) continue;
      result.push({
        key: `ack-review-${review.reviewId}`,
        id: review.reviewId,
        kind: 'review',
        title: review.cycle.title,
        due: null,
        overdue: false,
        href: `/performance/reviews/${review.reviewId}`,
      });
    }
    return [...new Map(result.map((item) => [item.key, item])).values()].sort(
      (a, b) =>
        Number(b.overdue) - Number(a.overdue) ||
        (a.due ?? '9999').localeCompare(b.due ?? '9999')
    );
  }, [
    tasks.data,
    pending.data,
    started.data,
    signatures.data,
    documentApprovals.data,
    acknowledgements.data,
    uploads.data,
    trainings.items,
    reviews.data,
    permissions,
    today,
    isLate,
  ]);

  const queries = [
    ...(includeTasks ? [tasks] : []),
    pending,
    started,
    signatures,
    documentApprovals,
    acknowledgements,
    ...(canReadDocuments ? [uploads] : []),
    reviews,
    trainings,
  ];
  return {
    rows,
    isLoading: queries.some((query) => query.isLoading),
    isError: queries.some((query) => query.isError),
    retry: () => Promise.all(queries.map((query) => query.refetch())),
  };
};
